import { useRecapReglements, useRecapSoldes } from '../api/hooks'
import type { LigneSolde, SemaineReglement } from '../api/types'

const fmtEur = (n: number) =>
  n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR', maximumFractionDigits: 2 })

export default function Comptes() {
  const q = useRecapSoldes()
  const reg = useRecapReglements()
  if (!q.data) return <p className="text-ocean-700 italic">Chargement…</p>
  const s = q.data
  const libelles = s.semaines_libelles ?? []

  const duMaison = (l: LigneSolde) => l.du_maison ?? l.du_nuitees
  const duMaisonMax = (l: LigneSolde) => l.du_maison_max ?? l.du_nuitees_max
  const duCourses = (l: LigneSolde) => l.du_courses ?? l.du_repas
  const soldeCourses = (l: LigneSolde) => l.solde_courses ?? (duCourses(l) - l.depenses_payees)

  // Lignes actives côté maison / côté courses
  const lignesMaison = s.lignes.filter(l => duMaison(l) > 0.005 || duMaisonMax(l) > 0.005)
  const lignesCourses = s.lignes.filter(
    l => duCourses(l) > 0.005 || l.depenses_payees > 0.005,
  )

  const totMaison = s.lignes.reduce((a, l) => a + duMaison(l), 0)
  const totMaisonMax = s.lignes.reduce((a, l) => a + duMaisonMax(l), 0)
  const totPaye = s.lignes.reduce((a, l) => a + l.depenses_payees, 0)
  const totDuCourses = s.lignes.reduce((a, l) => a + duCourses(l), 0)
  const aTentatifs = totMaisonMax > totMaison + 0.005

  return (
    <div className="space-y-4">
      {/* En-tête pédagogique : deux natures d'argent bien distinctes */}
      <section className="card p-4 sm:p-5">
        <h2 className="font-display text-lg sm:text-xl font-bold text-ocean-900 mb-1.5">
          🧮 Comptes
        </h2>
        <div className="grid sm:grid-cols-2 gap-2.5">
          <div className="rounded-lg bg-coral-50/60 border border-coral-100 px-3 py-2">
            <div className="text-sm font-bold text-coral-800 flex items-center gap-1.5">🏠 Maison</div>
            <p className="text-xs text-ocean-700 mt-0.5">
              Chaque famille règle sa part <strong>directement au propriétaire</strong> (un tiers),
              au prorata de ses nuits. Ces montants <strong>ne circulent pas</strong> entre les familles.
            </p>
          </div>
          <div className="rounded-lg bg-emerald-50/60 border border-emerald-100 px-3 py-2">
            <div className="text-sm font-bold text-emerald-800 flex items-center gap-1.5">🛒 Courses</div>
            <p className="text-xs text-ocean-700 mt-0.5">
              Réglées <strong>entre familles</strong>, <strong>semaine par semaine</strong> : qui a trop
              payé par rapport à ses repas est remboursé par qui a trop peu payé.
            </p>
          </div>
        </div>
        {aTentatifs && (
          <p className="text-[11px] text-amber-700 font-semibold mt-2">
            ↗ = Montant maison si toutes les réservations provisoires deviennent confirmées
          </p>
        )}
      </section>

      {/* ================= 🏠 MAISON — à verser au propriétaire ================= */}
      <section className="card overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-coral-100/70 bg-coral-50/40">
          <h3 className="text-base sm:text-lg font-bold text-coral-800 flex items-center gap-2">
            <span>🏠</span><span>Maison — à verser au propriétaire</span>
          </h3>
          <p className="text-xs text-ocean-700 mt-0.5">
            Part de chaque famille dans le loyer, réglée directement au propriétaire.
          </p>
        </div>

        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="table-clean">
            <thead>
              <tr>
                <th className="text-left px-4 py-2">Famille</th>
                {libelles.map((lib, i) => (
                  <th key={i} className="text-right px-3 py-2 whitespace-nowrap">{lib}</th>
                ))}
                <th className="text-right px-4 py-2">Dû maison</th>
              </tr>
            </thead>
            <tbody>
              {lignesMaison.map(l => (
                <tr key={l.compte_id}>
                  <td className="px-4 py-2 font-semibold text-ocean-900">{l.compte_nom}</td>
                  {libelles.map((_, i) => (
                    <td key={i} className="px-3 py-2 text-right text-ocean-700">
                      {(l.du_maison_semaine?.[i] ?? 0) > 0.005 ? fmtEur(l.du_maison_semaine![i]) : '—'}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right font-bold text-ocean-900 leading-tight">
                    <div>{fmtEur(duMaison(l))}</div>
                    {duMaisonMax(l) > duMaison(l) + 0.005 && (
                      <div className="text-[10px] text-amber-700 font-semibold">↗ {fmtEur(duMaisonMax(l))}</div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-coral-50/70">
              <tr>
                <td className="px-4 py-2 font-bold text-ocean-900">TOTAL</td>
                {libelles.map((_, i) => (
                  <td key={i} className="px-3 py-2 text-right font-bold text-ocean-900">
                    {fmtEur(s.lignes.reduce((a, l) => a + (l.du_maison_semaine?.[i] ?? 0), 0))}
                  </td>
                ))}
                <td className="px-4 py-2 text-right font-bold text-ocean-900 leading-tight">
                  <div>{fmtEur(totMaison)}</div>
                  {aTentatifs && <div className="text-[10px] text-amber-700 font-semibold">↗ {fmtEur(totMaisonMax)}</div>}
                </td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-ocean-50">
          {lignesMaison.length === 0 && (
            <div className="px-4 py-6 text-center text-ocean-500 italic">
              Pas encore de nuitées confirmées.
            </div>
          )}
          {lignesMaison.map(l => (
            <div key={l.compte_id} className="px-4 py-3 flex items-baseline justify-between gap-2">
              <div className="min-w-0">
                <h4 className="font-bold text-ocean-900">{l.compte_nom}</h4>
                <div className="flex flex-wrap gap-x-3 text-[11px] text-ocean-600 mt-0.5">
                  {libelles.map((lib, i) => (
                    (l.du_maison_semaine?.[i] ?? 0) > 0.005 && (
                      <span key={i}>{lib} : {fmtEur(l.du_maison_semaine![i])}</span>
                    )
                  ))}
                </div>
              </div>
              <div className="text-right leading-tight shrink-0">
                <span className="text-base font-bold text-ocean-900">{fmtEur(duMaison(l))}</span>
                {duMaisonMax(l) > duMaison(l) + 0.005 && (
                  <div className="text-[10px] text-amber-700 font-semibold">↗ {fmtEur(duMaisonMax(l))}</div>
                )}
              </div>
            </div>
          ))}
        </div>
        <p className="px-4 py-2.5 text-[11px] text-ocean-600/80 bg-coral-50/30 border-t border-coral-100 italic">
          Total à verser au propriétaire : <strong>{fmtEur(totMaison)}</strong>
          {aTentatifs && <> (jusqu'à {fmtEur(totMaisonMax)} si tous les provisoires se confirment)</>}.
        </p>
      </section>

      {/* ================= 🛒 COURSES — entre familles ================= */}
      <section className="card overflow-hidden">
        <div className="px-4 sm:px-5 py-3 border-b border-emerald-100/70 bg-emerald-50/40">
          <h3 className="text-base sm:text-lg font-bold text-emerald-800 flex items-center gap-2">
            <span>🛒</span><span>Courses — à régler entre familles</span>
          </h3>
          <p className="text-xs text-ocean-700 mt-0.5">
            <strong className="text-ocean-900">Solde positif</strong> = la famille doit encore aux autres.{' '}
            <strong className="text-coral-600">Négatif (rouge)</strong> = on lui doit (elle a trop avancé).
            Calcul <strong>semaine par semaine</strong>.
          </p>
        </div>

        {/* Desktop */}
        <div className="hidden md:block overflow-x-auto">
          <table className="table-clean">
            <thead>
              <tr>
                <th className="text-left px-4 py-2">Famille</th>
                <th className="text-right px-3 py-2">Payé (courses)</th>
                <th className="text-right px-3 py-2">Dû courses</th>
                <th className="text-right px-4 py-2">Solde courses</th>
              </tr>
            </thead>
            <tbody>
              {lignesCourses.map(l => {
                const sc = soldeCourses(l)
                const duSem = l.du_courses_semaine ?? []
                return (
                  <tr key={l.compte_id}>
                    <td className="px-4 py-2 font-semibold text-ocean-900">{l.compte_nom}</td>
                    <td className="px-3 py-2 text-right text-ocean-700">
                      {l.depenses_payees > 0.005 ? fmtEur(l.depenses_payees) : '—'}
                    </td>
                    <td className="px-3 py-2 text-right align-top leading-tight">
                      <div className="text-ocean-700">{duCourses(l) > 0.005 ? fmtEur(duCourses(l)) : '—'}</div>
                      {duSem.length > 1 && duCourses(l) > 0.005 && (
                        <div className="text-[10px] text-ocean-500">
                          {duSem.map((v, i) => v > 0.005 ? `S${i + 1} ${fmtEur(v)}` : null)
                            .filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                    <td className={`px-4 py-2 text-right font-bold leading-tight ${sc < -0.005 ? 'text-coral-600' : 'text-ocean-900'}`}>
                      <div>{Math.abs(sc) > 0.005 ? fmtEur(sc) : '—'}</div>
                      {(l.solde_courses_semaine?.length ?? 0) > 1 && Math.abs(sc) > 0.005 && (
                        <div className="text-[10px] font-semibold text-ocean-500">
                          {l.solde_courses_semaine!.map((v, i) => Math.abs(v) > 0.005 ? `S${i + 1} ${fmtEur(v)}` : null)
                            .filter(Boolean).join(' · ')}
                        </div>
                      )}
                    </td>
                  </tr>
                )
              })}
            </tbody>
            <tfoot className="bg-emerald-50/70">
              <tr>
                <td className="px-4 py-2 font-bold text-ocean-900">TOTAUX</td>
                <td className="px-3 py-2 text-right font-bold text-ocean-900">{fmtEur(totPaye)}</td>
                <td className="px-3 py-2 text-right font-bold text-ocean-900">{fmtEur(totDuCourses)}</td>
                <td className="px-4 py-2 text-right font-bold text-emerald-700">{fmtEur(0)}</td>
              </tr>
            </tfoot>
          </table>
        </div>

        {/* Mobile */}
        <div className="md:hidden divide-y divide-ocean-50">
          {lignesCourses.length === 0 && (
            <div className="px-4 py-6 text-center text-ocean-500 italic">
              Aucune course saisie pour l'instant.
            </div>
          )}
          {lignesCourses.map(l => {
            const sc = soldeCourses(l)
            return (
              <div key={l.compte_id} className="px-4 py-3">
                <div className="flex items-baseline justify-between mb-1">
                  <h4 className="font-bold text-ocean-900">{l.compte_nom}</h4>
                  <span className={`text-base font-bold ${sc < -0.005 ? 'text-coral-600' : 'text-ocean-900'}`}>
                    {Math.abs(sc) > 0.005 ? fmtEur(sc) : '—'}
                  </span>
                </div>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {l.depenses_payees > 0.005 && <Kv k="A payé" v={fmtEur(l.depenses_payees)} />}
                  {duCourses(l) > 0.005 && <Kv k="Dû courses" v={fmtEur(duCourses(l))} />}
                  {(l.solde_courses_semaine?.length ?? 0) > 1 &&
                    l.solde_courses_semaine!.map((v, i) => (
                      Math.abs(v) > 0.005 && <Kv key={i} k={`Solde S${i + 1}`} v={fmtEur(v)} />
                    ))}
                </div>
              </div>
            )
          })}
        </div>
        <p className="px-4 py-2.5 text-[11px] text-ocean-600/80 bg-emerald-50/30 border-t border-emerald-100 italic">
          Les courses s'équilibrent : la somme des soldes vaut 0. Les virements à faire sont
          proposés ci-dessous, séparément pour chaque semaine.
        </p>
      </section>

      {/* ================= 🤝 RÈGLEMENTS PAR SEMAINE ================= */}
      <section className="card overflow-hidden">
        <div className="px-4 sm:px-5 py-3 sm:py-4 border-b border-ocean-100/60">
          <h2 className="text-lg sm:text-xl font-bold text-ocean-900 flex items-center gap-2">
            <span>🤝</span>
            <span>Règlements proposés pour les courses</span>
          </h2>
          <p className="text-xs sm:text-sm text-ocean-700 mt-0.5">
            {reg.data && reg.data.total_depenses_courses > 0
              ? <>Le nombre minimum de virements à faire pour rembourser celles et ceux qui ont
                  avancé l'argent des courses — <strong>calculé séparément pour chaque semaine</strong>.</>
              : <>Dès que des <strong>dépenses de courses</strong> seront saisies dans
                  l'onglet <strong>🛒 Dépenses</strong>, une proposition de remboursement
                  s'affichera ici.</>}
          </p>
        </div>

        {reg.isLoading ? (
          <p className="px-4 py-5 text-sm text-ocean-700 italic">Chargement…</p>
        ) : !reg.data || reg.data.total_depenses_courses === 0 ? (
          <div className="px-4 py-6 text-center text-ocean-600">
            <div className="text-3xl mb-1">🥖</div>
            <p className="text-sm">Aucune dépense de courses pour l'instant — aucun remboursement à faire.</p>
          </div>
        ) : (
          <div className="divide-y divide-ocean-100">
            {(reg.data.semaines ?? []).map(sem => (
              <SemaineReglementBloc key={sem.index} sem={sem} />
            ))}
            {(reg.data.semaines ?? []).length === 0 && (
              <ReglementListe transactions={reg.data.transactions} />
            )}
          </div>
        )}
      </section>
    </div>
  )
}

/** Bloc de règlement d'UNE semaine : prix/repas + virements. */
function SemaineReglementBloc({ sem }: { sem: SemaineReglement }) {
  return (
    <div className="px-4 sm:px-5 py-3">
      <div className="flex items-center justify-between gap-2 mb-1.5">
        <h3 className="font-bold text-ocean-900 text-sm sm:text-base flex items-center gap-2">
          <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-emerald-100 text-emerald-800 text-xs font-bold">
            {sem.libelle}
          </span>
          {sem.total_depenses > 0.005 && (
            <span className="text-[11px] font-normal text-ocean-600">
              {fmtEur(sem.total_depenses)} · {sem.nb_repas} repas · {fmtEur(sem.prix_par_repas)}/repas
            </span>
          )}
        </h3>
      </div>

      {sem.total_depenses <= 0.005 ? (
        <p className="text-xs text-ocean-500 italic py-1">
          Aucune dépense de courses cette semaine.
        </p>
      ) : sem.transactions.length === 0 ? (
        <p className="text-xs text-emerald-700 font-semibold py-1 flex items-center gap-1">
          <span aria-hidden>✅</span> Tout est déjà équilibré sur cette semaine.
        </p>
      ) : (
        <>
          <ReglementListe transactions={sem.transactions} />
          <p className="text-[11px] text-ocean-600/80 mt-1.5">
            <strong>{sem.transactions.length}</strong> virement{sem.transactions.length > 1 ? 's' : ''} ·{' '}
            <strong>{sem.nb_debiteurs}</strong> famille{sem.nb_debiteurs > 1 ? 's' : ''} doi{sem.nb_debiteurs > 1 ? 'vent' : 't'} →{' '}
            <strong>{sem.nb_creanciers}</strong> à rembourser.
          </p>
        </>
      )}
    </div>
  )
}

function ReglementListe({ transactions }: { transactions: { de_compte_nom: string; vers_compte_nom: string; montant: number }[] }) {
  return (
    <ul className="space-y-1.5 mt-1">
      {transactions.map((t, i) => (
        <li key={i} className="flex items-center gap-3 rounded-lg bg-ocean-50/50 px-3 py-2">
          <span className="text-lg shrink-0" aria-hidden>💸</span>
          <div className="flex-1 flex items-center gap-2 flex-wrap text-sm">
            <span className="font-bold text-coral-700">{t.de_compte_nom}</span>
            <span className="text-ocean-500">doit</span>
            <span className="font-bold text-ocean-900 bg-white border border-ocean-100 px-2 py-0.5 rounded">{fmtEur(t.montant)}</span>
            <span className="text-ocean-500">à</span>
            <span className="font-bold text-emerald-700">{t.vers_compte_nom}</span>
          </div>
        </li>
      ))}
    </ul>
  )
}

function Kv({ k, v }: { k: string; v: string }) {
  return (
    <div className="flex justify-between items-baseline">
      <span className="text-ocean-600">{k}</span>
      <span className="font-medium text-ocean-900">{v}</span>
    </div>
  )
}
